import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { CalendarDays, Plus, Pencil, Trash2, RefreshCw, Eye, EyeOff, Download, Sparkles, Clock, Check, X, Repeat, CalendarCheck, Rss, Link2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatDateRange } from "@/components/EventCard";

const CATS = [
  { v: "concert", l: "Concert" }, { v: "spectacle", l: "Spectacle" },
  { v: "marche", l: "Marché" }, { v: "animation", l: "Animation" },
  { v: "sport", l: "Sport" }, { v: "expo", l: "Exposition" },
  { v: "fete", l: "Fête" }, { v: "atelier", l: "Atelier" },
  { v: "pro", l: "Pro / Entreprises" }, { v: "autre", l: "Autre" },
];

const EMPTY = {
  title: "", description: "", start_date: "", end_date: "",
  city: "", address: "", category: "animation", image: "", url: "",
  organizer: "", featured: false, status: "published",
};

function toLocalInputValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function AdminEvents() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [genDesc, setGenDesc] = useState(false);
  const [filter, setFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [sourceOptions, setSourceOptions] = useState([]);
  const [srcOpen, setSrcOpen] = useState(false);
  const [customSources, setCustomSources] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [activatingKey, setActivatingKey] = useState(null);
  const [newSrc, setNewSrc] = useState({ label: "", type: "ical", url: "", city: "", organizer: "" });
  const [addingSrc, setAddingSrc] = useState(false);

  const moderate = async (ev, action) => {
    try {
      await api.post(`/admin/events/${ev.id}/moderate`, { action });
      toast.success(action === "approve" ? "Événement publié ✅" : "Proposition refusée");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action impossible"); }
  };

  const syncHelloAsso = async () => {
    try {
      toast.message("Import HelloAsso en cours… (quelques secondes)");
      const { data } = await api.post("/admin/helloasso/sync");
      toast.success(`HelloAsso : ${data.events} événement(s) et ${data.orgs} association(s) importés.`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "HelloAsso indisponible (clé API manquante ?)"); }
  };

  const addHelloAsso = async () => {
    const raw = window.prompt("Colle le lien de la page HelloAsso de l'association (ex : https://www.helloasso.com/associations/mon-asso) ou son identifiant :");
    if (!raw) return;
    try {
      const { data } = await api.post("/admin/helloasso/sources", { slug: raw });
      toast.success(`Association ajoutée : ${data.name}. Lance l'import pour récupérer ses événements.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Association introuvable."); }
  };

  const loadSources = async () => {
    try { const { data } = await api.get("/admin/event-sources"); setCustomSources(data.sources || []); } catch { /* ignore */ }
    try { const { data } = await api.get("/admin/event-sources/catalog"); setCatalog(data.catalog || []); } catch { /* ignore */ }
  };
  const openSources = () => { setSrcOpen(true); loadSources(); };
  const activateCatalog = async (c) => {
    setActivatingKey(c.key);
    try {
      const { data } = await api.post("/admin/event-sources/activate", { key: c.key });
      toast.success(`${c.label} activé : ${data.found} événement(s) détecté(s).`);
      await loadSources();
    } catch (e) { toast.error(e?.response?.data?.detail || "Activation impossible (flux illisible ?)"); }
    finally { setActivatingKey(null); }
  };
  const addSource = async () => {
    if (!newSrc.label || !newSrc.url) { toast.error("Nom et URL du flux requis."); return; }
    setAddingSrc(true);
    try {
      const { data } = await api.post("/admin/event-sources", newSrc);
      toast.success(`Source ajoutée : ${data.found} événement(s) détecté(s).`);
      setNewSrc({ label: "", type: "ical", url: "", city: "", organizer: "" });
      await loadSources();
    } catch (e) { toast.error(e?.response?.data?.detail || "Ajout impossible (flux illisible ?)"); }
    finally { setAddingSrc(false); }
  };
  const importSource = async (key) => {
    try {
      const { data } = await api.post(`/admin/event-sources/${key}/import`);
      toast.success(`Import : ${data.inserted} ajouté(s), ${data.updated} mis à jour.`);
      await load(); await loadSources();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import impossible."); }
  };
  const deleteSource = async (key) => {
    if (!window.confirm("Supprimer cette source ?")) return;
    try { await api.delete(`/admin/event-sources/${key}`); toast.success("Source supprimée"); await loadSources(); }
    catch { toast.error("Erreur."); }
  };
  const toggleSource = async (s) => {
    try { await api.patch(`/admin/event-sources/${s.key}`, { active: s.active === false }); await loadSources(); }
    catch { toast.error("Erreur."); }
  };

  const runGenDesc = async () => {
    setGenDesc(true);
    try {
      const { data } = await api.post("/admin/events/generate-descriptions?limit=20");
      toast.success(`Descriptifs générés : ${data.generated} (sur ${data.candidates})`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Génération impossible"); }
    finally { setGenDesc(false); }
  };

  const runImport = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/admin/events/import");
      toast.success(`Import terminé : ${data.inserted} ajouté(s), ${data.updated} mis à jour`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'import."); }
    finally { setImporting(false); }
  };

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(filter === "pending" ? "/admin/events?status=pending" : "/admin/events");
      let rows = data.items || [];
      // Liste des sources distinctes présentes (pour le filtre admin)
      const seen = {};
      for (const e of rows) {
        const key = e.source || "autre";
        if (!seen[key]) seen[key] = { value: key, label: e.source_label || key };
      }
      setSourceOptions(Object.values(seen).sort((a, b) => a.label.localeCompare(b.label)));
      if (filter === "oneoff") rows = rows.filter((e) => !e.is_recurring);
      if (filter === "recurring") rows = rows.filter((e) => e.is_recurring);
      if (sourceFilter !== "all") rows = rows.filter((e) => (e.source || "autre") === sourceFilter);
      setItems(rows);
    } catch { toast.error("Erreur chargement."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter, sourceFilter]); // eslint-disable-line

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (ev) => {
    setEditing(ev);
    setForm({
      ...EMPTY, ...ev,
      start_date: toLocalInputValue(ev.start_date),
      end_date: toLocalInputValue(ev.end_date),
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.start_date || !form.city) {
      toast.error("Titre, date de début et ville sont obligatoires.");
      return;
    }
    const payload = {
      ...form,
      start_date: new Date(form.start_date).toISOString(),
      end_date: form.end_date ? new Date(form.end_date).toISOString() : "",
    };
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/admin/events/${editing.id}`, payload);
        toast.success("Événement mis à jour");
      } else {
        await api.post("/admin/events", payload);
        toast.success("Événement créé");
      }
      setOpen(false); setForm(EMPTY); setEditing(null);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur."); }
    finally { setSaving(false); }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Supprimer « ${ev.title} » ?`)) return;
    try {
      await api.delete(`/admin/events/${ev.id}`);
      toast.success("Supprimé");
      await load();
    } catch { toast.error("Erreur."); }
  };

  return (
    <div data-testid="admin-events-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2" data-testid="admin-events-title">
            <CalendarDays className="text-rose-600" size={26}/> Événements locaux
          </h1>
          <p className="text-slate-500 text-xs mt-1">Gérez l'agenda local « Sortir en Eure-et-Loir ».</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="admin-events-reload">
            <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`}/> Actualiser
          </Button>
          <Button variant="outline" onClick={runImport} disabled={importing} data-testid="admin-events-import">
            <Download size={14} className={`mr-1 ${importing ? "animate-pulse" : ""}`}/> {importing ? "Import…" : "Importer (communes)"}
          </Button>
          <Button variant="outline" onClick={runGenDesc} disabled={genDesc} data-testid="admin-events-gendesc">
            <Sparkles size={14} className={`mr-1 ${genDesc ? "animate-pulse" : ""}`}/> {genDesc ? "Génération…" : "Générer descriptifs (IA)"}
          </Button>
          <Button onClick={openNew} className="btn-shine text-white" data-testid="admin-events-new">
            <Plus size={14} className="mr-1"/> Nouvel événement
          </Button>
        </div>
      </div>

      <div className="mt-4 inline-flex rounded-xl bg-white border border-slate-200 p-1 gap-1 flex-wrap" data-testid="admin-events-filter">
        <button onClick={() => setFilter("all")} data-testid="admin-events-filter-all"
          className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${filter === "all" ? "bg-[#E05638] text-white" : "text-slate-600 hover:bg-slate-100"}`}>Tous</button>
        <button onClick={() => setFilter("oneoff")} data-testid="admin-events-filter-oneoff"
          className={`px-3 py-1.5 rounded-lg text-sm font-semibold inline-flex items-center gap-1 ${filter === "oneoff" ? "bg-[#E05638] text-white" : "text-slate-600 hover:bg-slate-100"}`}><CalendarCheck size={14} /> Ponctuels</button>
        <button onClick={() => setFilter("recurring")} data-testid="admin-events-filter-recurring"
          className={`px-3 py-1.5 rounded-lg text-sm font-semibold inline-flex items-center gap-1 ${filter === "recurring" ? "bg-[#E05638] text-white" : "text-slate-600 hover:bg-slate-100"}`}><Repeat size={14} /> Récurrents</button>
        <button onClick={() => setFilter("pending")} data-testid="admin-events-filter-pending"
          className={`px-3 py-1.5 rounded-lg text-sm font-semibold inline-flex items-center gap-1 ${filter === "pending" ? "bg-[#E05638] text-white" : "text-slate-600 hover:bg-slate-100"}`}><Clock size={14} /> À valider</button>
      </div>
      <div className="mt-4 ml-2 inline-flex align-top">
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="h-[38px] w-[220px] bg-white" data-testid="admin-events-source-filter">
            <SelectValue placeholder="Toutes les sources" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les sources</SelectItem>
            {sourceOptions.map((s) => (
              <SelectItem key={s.value} value={s.value} data-testid={`admin-events-source-option-${s.value}`}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button variant="outline" size="sm" className="mt-4 ml-2 align-top" onClick={addHelloAsso} data-testid="admin-helloasso-add">
        <Plus size={14} className="mr-1" /> Suivre une asso HelloAsso
      </Button>
      <Button variant="outline" size="sm" className="mt-4 ml-2 align-top" onClick={syncHelloAsso} data-testid="admin-helloasso-sync">
        <Download size={14} className="mr-1" /> Importer HelloAsso (28)
      </Button>
      <Button size="sm" className="mt-4 ml-2 align-top btn-shine text-white" onClick={openSources} data-testid="admin-sources-manage">
        <Plus size={14} className="mr-1" /> Ajouter une source (agenda iCal / RSS)
      </Button>

      {loading ? <div className="mt-6 text-slate-500 text-sm">Chargement...</div> :
       items.length === 0 ? <div className="mt-6 bg-white border border-dashed rounded-2xl p-10 text-center text-slate-500 text-sm">Aucun événement pour l'instant. Créez le premier !</div> :
       <div className="mt-5 space-y-3">
         {items.map((ev) => (
           <div key={ev.id} className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-wrap gap-4" data-testid={`admin-event-row-${ev.id}`}>
             <div className="w-24 h-20 rounded-lg bg-slate-100 overflow-hidden shrink-0">
               {ev.image ? <img src={ev.image} alt={ev.title} className="w-full h-full object-cover"/> :
                 <div className="w-full h-full grid place-items-center text-slate-300"><CalendarDays size={28}/></div>}
             </div>
             <div className="flex-1 min-w-0">
               <div className="flex items-center gap-2 flex-wrap">
                 <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${ev.status === "published" ? "bg-emerald-100 text-emerald-800" : ev.status === "pending" ? "bg-amber-100 text-amber-800" : ev.status === "refused" ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"}`}>
                   {ev.status === "published" ? <><Eye size={11}/> Publié</> : ev.status === "pending" ? <><Clock size={11}/> À valider</> : ev.status === "refused" ? <><X size={11}/> Refusé</> : <><EyeOff size={11}/> Brouillon</>}
                 </span>
                 <span className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-bold uppercase">{ev.category_label}</span>
                 <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded font-bold uppercase ${ev.is_recurring ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-700"}`} data-testid={`admin-event-kind-${ev.id}`}>
                   {ev.is_recurring ? <><Repeat size={10}/> Récurrent</> : <><CalendarCheck size={10}/> Ponctuel</>}
                 </span>
                 {ev.source_label && ev.source !== "user" && <span className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-600 font-semibold">via {ev.source_label}</span>}
                 {ev.featured && <span className="text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-bold uppercase">Mis en avant</span>}
                 {ev.source === "user" && <span className="text-[10px] px-2 py-0.5 rounded bg-indigo-100 text-indigo-800 font-bold uppercase">Proposé par un membre</span>}
               </div>
               <div className="mt-1 font-heading font-bold text-slate-900">{ev.title}</div>
               <div className="text-xs text-slate-600 mt-0.5">📅 {formatDateRange(ev.start_date, ev.end_date)}</div>
               <div className="text-xs text-slate-600">📍 {ev.city}{ev.address ? ` — ${ev.address}` : ""}</div>
               {ev.source === "user" && ev.proposer_name && <div className="text-xs text-indigo-700 mt-0.5">Proposé par {ev.proposer_name}{ev.proposer_email ? ` (${ev.proposer_email})` : ""}</div>}
             </div>
             <div className="flex items-start gap-2">
               {ev.status === "pending" && (
                 <>
                   <Button size="sm" onClick={() => moderate(ev, "approve")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`admin-event-approve-${ev.id}`}>
                     <Check size={13} className="mr-1"/> Valider
                   </Button>
                   <Button size="sm" variant="outline" onClick={() => moderate(ev, "reject")} className="text-rose-600 border-rose-200" title="Refuser" data-testid={`admin-event-reject-${ev.id}`}>
                     <X size={13}/>
                   </Button>
                 </>
               )}
               <Button size="sm" variant="outline" onClick={() => openEdit(ev)} data-testid={`admin-event-edit-${ev.id}`}>
                 <Pencil size={13}/>
               </Button>
               <Button size="sm" variant="outline" onClick={() => remove(ev)} className="text-rose-600" data-testid={`admin-event-delete-${ev.id}`}>
                 <Trash2 size={13}/>
               </Button>
             </div>
           </div>
         ))}
       </div>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-event-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier l'événement" : "Nouvel événement"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Titre *</Label>
              <Input value={form.title} onChange={(e) => set("title", e.target.value)} required data-testid="admin-event-title"/>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Date & heure de début *</Label>
                <Input type="datetime-local" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} required data-testid="admin-event-start"/>
              </div>
              <div>
                <Label>Date & heure de fin</Label>
                <Input type="datetime-local" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} data-testid="admin-event-end"/>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Ville *</Label>
                <Input value={form.city} onChange={(e) => set("city", e.target.value)} required data-testid="admin-event-city"/>
              </div>
              <div>
                <Label>Catégorie *</Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger data-testid="admin-event-category"><SelectValue/></SelectTrigger>
                  <SelectContent>{CATS.map((c) => (<SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Adresse (facultatif)</Label>
              <Input value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="admin-event-address"/>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="admin-event-desc"/>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Image (URL)</Label>
                <Input value={form.image} onChange={(e) => set("image", e.target.value)} placeholder="https://..." data-testid="admin-event-image"/>
              </div>
              <div>
                <Label>Lien externe (facultatif)</Label>
                <Input value={form.url} onChange={(e) => set("url", e.target.value)} placeholder="https://..." data-testid="admin-event-url"/>
              </div>
            </div>
            <div>
              <Label>Organisateur</Label>
              <Input value={form.organizer} onChange={(e) => set("organizer", e.target.value)} data-testid="admin-event-organizer"/>
            </div>
            <div className="flex flex-wrap items-center gap-4 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={form.featured} onCheckedChange={(v) => set("featured", !!v)} data-testid="admin-event-featured"/>
                Mettre en avant
              </label>
              <label className="flex items-center gap-2 text-sm ml-auto">
                <span>Statut :</span>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger className="w-40" data-testid="admin-event-status"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="published">Publié</SelectItem>
                    <SelectItem value="draft">Brouillon</SelectItem>
                  </SelectContent>
                </Select>
              </label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={saving}>Annuler</Button>
              <Button type="submit" disabled={saving} className="btn-shine text-white" data-testid="admin-event-submit">
                {saving ? "Enregistrement..." : (editing ? "Mettre à jour" : "Créer l'événement")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={srcOpen} onOpenChange={setSrcOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-sources-dialog">
          <DialogHeader>
            <DialogTitle>Sources d'événements personnalisées</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">
            Ajoutez l'agenda d'une commune, d'un office de tourisme ou d'une salle via son flux <b>iCal (.ics)</b> (recommandé) ou <b>RSS</b>. Les sorties/événements seront récupérés à chaque import.
          </p>

          {catalog.length > 0 && (
            <div className="mt-1 rounded-xl border border-orange-200 bg-orange-50/60 p-3" data-testid="official-sources-28">
              <div className="flex items-center gap-2 font-heading font-extrabold text-slate-900 text-sm">
                <CalendarCheck size={16} className="text-[#E05638]" /> Sources officielles 28 — activation en un clic
              </div>
              <p className="mt-0.5 text-xs text-slate-500">Agendas des mairies et offices de tourisme d'Eure-et-Loir, prêts à l'emploi.</p>
              <div className="mt-2.5 space-y-2">
                {catalog.map((c) => (
                  <div key={c.key} className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-2.5" data-testid={`official-source-${c.key}`}>
                    <span className="w-8 h-8 rounded-lg bg-orange-100 grid place-items-center text-[#E05638] shrink-0">
                      {c.type === "rss" ? <Rss size={15} /> : <CalendarDays size={15} />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 text-sm truncate">{c.label}</div>
                      <div className="text-xs text-slate-500 truncate">{c.note}{c.city ? ` · ${c.city}` : ""}</div>
                      {c.added && typeof c.last_count === "number" && (
                        <div className="text-[11px] text-emerald-600">{c.last_count} événement(s) détecté(s){c.active ? "" : " · inactive"}</div>
                      )}
                    </div>
                    {c.added ? (
                      <span className="text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-emerald-200 text-emerald-700 bg-emerald-50 shrink-0 inline-flex items-center gap-1" data-testid={`official-source-added-${c.key}`}>
                        <Check size={13} /> Activée
                      </span>
                    ) : (
                      <Button size="sm" className="btn-shine text-white shrink-0" disabled={activatingKey === c.key}
                        onClick={() => activateCatalog(c)} data-testid={`official-source-activate-${c.key}`}>
                        {activatingKey === c.key ? <><Loader2 size={13} className="mr-1 animate-spin" /> Test…</> : <><Plus size={13} className="mr-1" /> Activer</>}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-3 bg-slate-50 border border-slate-200 rounded-xl p-3">
            <div className="md:col-span-2">
              <Label>Nom de la source *</Label>
              <Input value={newSrc.label} onChange={(e) => setNewSrc((s) => ({ ...s, label: e.target.value }))} placeholder="Ex : Ville de Lucé" data-testid="admin-source-label" />
            </div>
            <div>
              <Label>Type de flux *</Label>
              <Select value={newSrc.type} onValueChange={(v) => setNewSrc((s) => ({ ...s, type: v }))}>
                <SelectTrigger data-testid="admin-source-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ical">iCal (.ics) — recommandé</SelectItem>
                  <SelectItem value="rss">RSS / Atom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Ville par défaut</Label>
              <Input value={newSrc.city} onChange={(e) => setNewSrc((s) => ({ ...s, city: e.target.value }))} placeholder="Ex : Lucé" data-testid="admin-source-city" />
            </div>
            <div className="md:col-span-2">
              <Label>URL du flux *</Label>
              <Input value={newSrc.url} onChange={(e) => setNewSrc((s) => ({ ...s, url: e.target.value }))} placeholder="https://… .ics ou flux RSS" data-testid="admin-source-url" />
            </div>
            <div className="md:col-span-2">
              <Label>Organisateur (facultatif)</Label>
              <Input value={newSrc.organizer} onChange={(e) => setNewSrc((s) => ({ ...s, organizer: e.target.value }))} placeholder="Ex : Mairie de Lucé" data-testid="admin-source-organizer" />
            </div>
            <div className="md:col-span-2">
              <Button onClick={addSource} disabled={addingSrc} className="btn-shine text-white w-full" data-testid="admin-source-add">
                {addingSrc ? <><Loader2 size={14} className="mr-1 animate-spin" /> Test du flux…</> : <><Plus size={14} className="mr-1" /> Ajouter & tester la source</>}
              </Button>
            </div>
          </div>

          <div className="mt-2 space-y-2">
            {customSources.length === 0 ? (
              <div className="text-sm text-slate-400 text-center py-4" data-testid="admin-sources-empty">Aucune source personnalisée pour l'instant.</div>
            ) : customSources.map((s) => (
              <div key={s.key} className={`flex items-center gap-3 border border-slate-200 rounded-xl p-3 ${s.active === false ? "opacity-60 bg-slate-50" : ""}`} data-testid={`admin-source-row-${s.key}`}>
                <span className="w-8 h-8 rounded-lg bg-slate-100 grid place-items-center text-slate-500 shrink-0">{s.type === "rss" ? <Rss size={15} /> : <Link2 size={15} />}</span>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 text-sm truncate">{s.label} <span className="text-[10px] uppercase text-slate-400">({s.type})</span></div>
                  <div className="text-xs text-slate-500 truncate">{s.url}</div>
                  {typeof s.last_count === "number" && <div className="text-[11px] text-emerald-600">{s.last_count} événement(s) détecté(s)</div>}
                </div>
                <button onClick={() => toggleSource(s)} data-testid={`admin-source-toggle-${s.key}`}
                  className={`text-xs font-semibold px-2.5 py-1.5 rounded-lg border shrink-0 ${s.active === false ? "border-slate-200 text-slate-500 bg-white" : "border-emerald-200 text-emerald-700 bg-emerald-50"}`}>
                  {s.active === false ? "Inactive" : "Active"}
                </button>
                <Button size="sm" variant="outline" onClick={() => importSource(s.key)} data-testid={`admin-source-import-${s.key}`}><Download size={13} className="mr-1" /> Importer</Button>
                <Button size="sm" variant="outline" className="text-rose-600" onClick={() => deleteSource(s.key)} data-testid={`admin-source-delete-${s.key}`}><Trash2 size={13} /></Button>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSrcOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
